import { GameConfig } from "@/config/GameConfig";
import { isQaModeAllowedHost } from "@/qa/QaMode";

/**
 * QA-only Daily Challenge affordances: load a specific challenge date,
 * inspect the generated spec, reset local UI state, and simulate
 * attempts remaining - all gated by the exact same strict hostname
 * allowlist `?qa=` campaign QA modes already use
 * (`isQaModeAllowedHost` - local dev or this project's own Deploy
 * Preview pattern). It is impossible to read any of this on production:
 * every getter below checks the host FIRST and returns the
 * production-safe default (no override) otherwise - the same
 * "the host gate is inseparable from reading the value" rule
 * `QaMode.resolveQaMode` already follows.
 *
 * The date override itself is only ever a REQUEST - the real
 * enforcement is server-side (`netlify/functions/daily-challenge.ts`
 * checks Netlify's own `process.env.CONTEXT`, which a client can't
 * forge), this client-side gate exists purely so the QA controls don't
 * even appear/act on production.
 */

interface DailyQaState {
  qaDate?: string;
  simulatedAttemptsRemaining?: number;
}

function isValid(value: unknown): value is DailyQaState {
  if (!value || typeof value !== "object") return false;
  const c = value as Partial<DailyQaState>;
  return (c.qaDate === undefined || typeof c.qaDate === "string") && (c.simulatedAttemptsRemaining === undefined || typeof c.simulatedAttemptsRemaining === "number");
}

function readState(): DailyQaState {
  try {
    const raw = globalThis.localStorage?.getItem(GameConfig.storage.dailyQaKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return isValid(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeState(state: DailyQaState): void {
  try {
    globalThis.localStorage?.setItem(GameConfig.storage.dailyQaKey, JSON.stringify(state));
  } catch {
    // QA-only convenience - safe to skip on storage failure.
  }
}

/** Whether the current hostname is allowed to use any Daily Challenge QA affordance at all. Every other function in this module already checks this itself - exported so UI code can decide whether to render the QA controls in the first place. */
export function isDailyQaAllowedHost(hostname: string | null | undefined): boolean {
  return isQaModeAllowedHost(hostname);
}

const QA_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `challengeId ± deltaDays`, as a fresh `"YYYY-MM-DD"` string - pure `Date.UTC` arithmetic (never host-timezone-dependent), used only by the QA panel's PREVIOUS DAY/NEXT DAY controls for rapid visual review across consecutive dates. Not gated on `isQaModeAllowedHost` itself (it's pure date math, no state read/written) - the caller always still routes the result through `setDailyQaDate()`, which is gated. */
export function addDaysToChallengeId(challengeId: string, deltaDays: number): string {
  const [y, m, d] = challengeId.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d) + deltaDays * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/** The QA-requested challenge date to send with the `daily-challenge` fetch, or `undefined` on any disallowed host / when nothing was set. */
export function getDailyQaDate(hostname: string | null | undefined): string | undefined {
  if (!isQaModeAllowedHost(hostname)) return undefined;
  const value = readState().qaDate;
  return value && QA_DATE_RE.test(value) ? value : undefined;
}

/** Sets (or clears, passing `null`) the QA-requested challenge date. A no-op on a disallowed host - never persists a value that couldn't be read back on production either. */
export function setDailyQaDate(hostname: string | null | undefined, date: string | null): void {
  if (!isQaModeAllowedHost(hostname)) return;
  const state = readState();
  if (date && QA_DATE_RE.test(date)) state.qaDate = date;
  else delete state.qaDate;
  writeState(state);
}

/** A locally-simulated "attempts remaining" the pre-run screen may display instead of the server's real value, for exercising every attempts-remaining UI state on demand - never sent to, or trusted from, the backend; the real reservation call always uses the server's own authoritative count regardless of this. `undefined` on any disallowed host / when nothing was set. */
export function getSimulatedAttemptsRemaining(hostname: string | null | undefined): number | undefined {
  if (!isQaModeAllowedHost(hostname)) return undefined;
  const value = readState().simulatedAttemptsRemaining;
  return typeof value === "number" && value >= 0 && value <= 3 ? value : undefined;
}

export function setSimulatedAttemptsRemaining(hostname: string | null | undefined, value: number | null): void {
  if (!isQaModeAllowedHost(hostname)) return;
  const state = readState();
  if (value !== null && Number.isInteger(value) && value >= 0 && value <= 3) state.simulatedAttemptsRemaining = value;
  else delete state.simulatedAttemptsRemaining;
  writeState(state);
}

/** Clears every QA override - a no-op on a disallowed host. */
export function resetDailyQaState(hostname: string | null | undefined): void {
  if (!isQaModeAllowedHost(hostname)) return;
  try {
    globalThis.localStorage?.removeItem(GameConfig.storage.dailyQaKey);
  } catch {
    // Ignore.
  }
}
